import { useEffect, useState } from 'react';

/**
 * True while a finger/pointer is dragging the krpano viewer (`#pano`), plus a
 * short tail after release so inertia finishes smoothly. Used to suspend
 * competing WebGL work (the 3D avatar) during 360° interaction on weak GPUs.
 * Only flips state at gesture start/end, never per move event.
 */
export function useKrpanoInteracting(enabled: boolean, tailMs = 400): boolean {
  const [interacting, setInteracting] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let tail: ReturnType<typeof setTimeout> | null = null;
    const onDown = (e: Event) => {
      if (!(e.target instanceof Element) || !e.target.closest('#pano')) return;
      if (tail) { clearTimeout(tail); tail = null; }
      setInteracting(true);
    };
    const onUp = () => {
      if (tail) clearTimeout(tail);
      tail = setTimeout(() => { tail = null; setInteracting(false); }, tailMs);
    };
    document.addEventListener('pointerdown', onDown, { capture: true, passive: true });
    document.addEventListener('pointerup', onUp, { capture: true, passive: true });
    document.addEventListener('pointercancel', onUp, { capture: true, passive: true });
    return () => {
      if (tail) clearTimeout(tail);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('pointerup', onUp, true);
      document.removeEventListener('pointercancel', onUp, true);
    };
  }, [enabled, tailMs]);

  return enabled && interacting;
}
